import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useScreenWatch, useMetadataWatchStore } from '../model/metadataWatchStore';

interface ScreenWatchContext {
  screenId: string | null;
  /** Vurgulanacak alan adları (küçük harf). */
  highlights: ReadonlySet<string>;
}

/**
 * Ekranın metadata-izleme bağlamı: hangi ekran ve hangi kolonlar vurgulanacak.
 *
 * Context, prop olarak taşımaya yeğleniyor: vurgu ve düzenleme kilidi grid'in
 * mimarisiyle ilgisi olmayan çapraz kesen ilgiler ve DynamicGrid'in her kullanım yerine
 * (master, detay, monitor, form builder…) prop eklemek kalıcı bir yük olurdu.
 */
const Ctx = createContext<ScreenWatchContext>({ screenId: null, highlights: new Set() });

export function MetadataHighlightProvider({
  screenId,
  children,
}: {
  screenId: string | null | undefined;
  children: ReactNode;
}) {
  const watch = useScreenWatch(screenId);
  const value = useMemo(
    () => ({ screenId: screenId ?? null, highlights: new Set(watch.highlights.map((f) => f.toLowerCase())) }),
    [screenId, watch.highlights],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Vurgulanacak alan adları.
 *
 * Set döndürüyor, tek alan sormuyor: çağıran bir kolon listesi üzerinde dönüyor ve
 * kolon başına hook çağrılamaz.
 */
export function useHighlightedFields(): ReadonlySet<string> {
  return useContext(Ctx).highlights;
}

/**
 * Ekranda düzenleme sürdüğü sürece metadata tazelemesini bekletir.
 *
 * Kullanıcı satır düzenlerken kolonların altından değişmesi girdiği veriyi götürürdü;
 * bu yüzden düzenleme varken tazeleme otomatik yapılmaz, şeritle sorulur.
 */
export function useEditLock(active: boolean): void {
  const { screenId } = useContext(Ctx);
  useEffect(() => {
    if (!screenId || !active) return;
    const { lockEdit, unlockEdit } = useMetadataWatchStore.getState();
    lockEdit(screenId);
    return () => unlockEdit(screenId);
  }, [screenId, active]);
}

/** Ekranda açık bir düzenleme var mı (form, satır içi düzenleme) — canlı satır senkronu bekletilir. */
export function useScreenEditLocked(): boolean {
  const { screenId } = useContext(Ctx);
  return useMetadataWatchStore((s) => (screenId ? (s.editLocks[screenId.toLowerCase()] ?? 0) > 0 : false));
}
